import type React from "react";
import { toast } from "sonner";

import { stripSkillGrouping } from "../../../handlers/utilityFunctions";

export interface SkillListItem {
	id: number | string;
	title: string;
	added: boolean;
}

export interface GroupedSkill {
	title: string;
	skills: Array<{ added: boolean; skill: string }>;
	collapsed: boolean;
}

interface SkillsEditorListItemsProps {
	editorListItems: SkillListItem[];
	editorContent: string;
	setEditorContent: (content: string) => void;
	setEditorListItems: (items: SkillListItem[]) => void;
	groupedSkills: GroupedSkill[];
	setGroupedSkills: (groupedSkills: GroupedSkill[]) => void;
}

const SkillsEditorListItems = (props: SkillsEditorListItemsProps) => {
	const editorListHandler = (event: React.MouseEvent<HTMLLIElement>) => {
		const target = event.target as HTMLElement;
		//get the text content of the selected li
		let selectedItem = target.textContent;
		let selectedItemID = target.id;

		/** if the selected item is button inside the li
		 * then get the text content of the respective li
		 */
		if (target.tagName === "I" || target.tagName === "BUTTON") {
			selectedItem = target.closest("li").firstChild.textContent;
			selectedItemID = (event.currentTarget as HTMLElement).closest("li").id;
		}

		// Remove the button text from the li text content, and drop the grouping
		// punctuation the source data wraps each category in. The editor used to show
		// "Research: { Documentation, Data collection[ Interviews ] }" while the save
		// stored the tidied version, so what the reader saw never matched the resume.
		const liContent = stripSkillGrouping(selectedItem);
		let newEditorContent = "";

		//check if the editor content has an existing list
		const hasUl = props.editorContent.indexOf("<ul>");

		//if it has an existing list already
		if (hasUl !== -1) {
			//if the clicked item is present in the list
			if (props.editorContent.indexOf(liContent) !== -1) {
				//remove the item from the editor&#39;s list
				newEditorContent = props.editorContent.replace(`<li>${liContent}</li>`, "");
			} else {
				//add the item to the editors list
				newEditorContent = props.editorContent
					.substring(0, props.editorContent.indexOf("</ul>"))
					.concat("<li>", liContent, "</li></ul>");
			}
		} else {
			//create a new list and add a li
			newEditorContent = props.editorContent.concat("<ul><li>", liContent, "</li></ul>");
		}

		// //copy the editor list items
		const changedList = [...props.editorListItems];

		// //find the item that was clicked in the editors list items
		const indexChanged = changedList.findIndex((changed) => Number(changed.id) === Number(selectedItemID));

		// //check if skill already added
		const splitArray = changedList[indexChanged].title.split(/:(.+)/);
		let wordBeforeColon = splitArray[0].trim();
		const skillExistIndex = props.groupedSkills.findIndex((item) => item.title.trim() === wordBeforeColon);
		if (skillExistIndex !== -1) {
			let newGroupedSkills = [...props.groupedSkills];
			newGroupedSkills[skillExistIndex].collapsed = false;
			props.setGroupedSkills(newGroupedSkills);
			toast.error(wordBeforeColon + " exist to add more item under it click Add custom button", {
				duration: 12000,
			});
			return;
		}

		//changed the added property of the clicked editors item to add and remove the css class
		changedList[indexChanged].added = !changedList[indexChanged].added;

		props.setEditorContent(newEditorContent);
		let groupedSkills = [];
		if (splitArray.length > 1) {
			groupedSkills = groupedSkills.concat(
				splitArray[1]
					.replace(/\([^)]*\)/g, "")
					.split(",")
					.map((skillItem) => {
						let value = skillItem.replace("[", "").replace("]", "").replace("{", "").replace("}", "").trim();
						// `added` means "goes into the resume", and the text this same click
						// writes into the editor already lists every one of these. Marking them
						// false made the two disagree: the editor showed the full category while
						// the save built its payload from `groupedSkills` filtered on `added`,
						// so the server only ever received the bare title — "Marketing Strategy: ".
						return { added: true, skill: value };
					}),
			);
		}
		const addedSkill = { title: wordBeforeColon, skills: groupedSkills, collapsed: false };
		let newGroupedSkills = props.groupedSkills;
		if (changedList[indexChanged].added) {
			newGroupedSkills = [...newGroupedSkills, addedSkill];
		} else {
			newGroupedSkills = newGroupedSkills.filter((skillItem) => skillItem.title !== wordBeforeColon);
		}
		props.setGroupedSkills(newGroupedSkills);
	};

	const theList = props.editorListItems.map((editorListItem) => {
		return (
			<li
				onClick={editorListHandler}
				key={editorListItem.id}
				id={String(editorListItem.id)}
				className="flex cursor-pointer items-start justify-between border-black/10 border-b py-3 text-sm"
			>
				{editorListItem.title}
				<button
					type="button"
					id={String(editorListItem.id)}
					tabIndex={-1}
					className="ml-3 flex size-7 shrink-0 items-center justify-center rounded p-2.5"
					style={{ background: "#d6e7ff", ...(editorListItem.added && { background: "#28B870" }) }}
				>
					{!editorListItem.added && <i className="fa fa-plus" />}
					{editorListItem.added && <i className="fa fa-check text-white" />}
				</button>
			</li>
		);
	});
	return <ul className="m-0 list-none px-3">{theList}</ul>;
};

export default SkillsEditorListItems;
